import { UserRole, type PatchBuildingByIdRequestBody } from "@eisox/api/core-api";
import { useAppForm } from "@eisox/form";
import { getDirtyValues } from "@eisox/tools";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { buildingApi } from "~/api";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/bms/pages")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { building, boilerRooms, airHandlerUnits, role } = useBuildingContext(Route.id);

  const { mutateAsync } = useMutation({
    mutationFn: (body: PatchBuildingByIdRequestBody) => buildingApi.update(building.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries(buildingApi.queries.byId(building.id));
    },
  });

  const form = useAppForm({
    defaultValues: {
      boilerRooms: building.pages.boilerRooms,
      airHandlerUnits: building.pages.airHandlerUnits,
    },
    onSubmit: async ({ value, formApi }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      await mutateAsync({ pages: dirty as NonNullable<PatchBuildingByIdRequestBody["pages"]> });
      formApi.reset();
    },
  });

  if (role !== UserRole.INSTALLER) throw new Error("Unauthorized");

  return (
    <form
      className="gap-y-5 flex h-full flex-col"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <div className="max-w-100 gap-y-2.5 flex flex-col">
          {boilerRooms.length > 0 && (
            <div className="gap-2.5 flex flex-col">
              <span className="font-medium text-base text-primary-dark-blue">{`${m.page()} ${m.boiler_room()}`}</span>
              <form.AppField name="boilerRooms">{field => <field.Switch />}</form.AppField>
            </div>
          )}
          {airHandlerUnits.length > 0 && (
            <div className="gap-2.5 flex flex-col">
              <span className="font-medium text-base text-primary-dark-blue">
                {`${m.page()} ${m.air_handler_unit()}`}
              </span>
              <form.AppField name="airHandlerUnits">{field => <field.Switch />}</form.AppField>
            </div>
          )}
        </div>
        <div className="mt-auto flex w-full items-center justify-center">
          <form.SubmitButton label={m.save()} />
        </div>
      </form.AppForm>
    </form>
  );
}
